import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import EmptyMessageComponent from '../EmptyMessageComponent';

interface ScatterPlotSentimentProps {
    data: any[];
}

const ScatterPlotSentiment: React.FC<ScatterPlotSentimentProps> = ({ data }) => {
    // Process data to create scatter points
    const processedData = React.useMemo(() => {
        if (!data || data.length === 0) return { series: [], options: {} };

        // Get color for each sentiment
        const getSentimentColor = (sentiment: string) => {
            switch (sentiment) {
                case 'good': return '#22c55e';
                case 'neutral': return '#f59e0b';
                case 'bad': return '#ef4444';
                default: return '#6b7280';
            }
        };

        // Get sentiment label for display
        const getSentimentLabel = (sentiment: string) => {
            switch (sentiment) {
                case 'bad': return 'Negative';
                case 'neutral': return 'Neutral';
                case 'good': return 'Positive';
                default: return 'Unknown';
            }
        };

        // Process all reviews into scatter points
        const scatterPoints = data.map((review: any) => {
            const sentiment = review.sentiment?.toLowerCase() || 'unknown';
            const rating = Number(review.rating) || 3;
            const reviewDate = new Date(review.updated);
            
            return {
                x: reviewDate.getTime(), // Time in milliseconds
                y: rating, // Rating (1-5)
                date: reviewDate.toLocaleDateString(),
                sentiment: sentiment,
                sentimentLabel: getSentimentLabel(sentiment),
                originalRating: rating
            };
        });

        // Group by sentiment for series
        const sentimentGroups = scatterPoints.reduce((acc: any, point: any) => {
            const key = point.sentiment;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(point);
            return acc;
        }, {});

        // Create series for ApexCharts
        const series = Object.keys(sentimentGroups).map((sentiment) => ({
            name: getSentimentLabel(sentiment),
            data: sentimentGroups[sentiment],
            color: getSentimentColor(sentiment)
        }));

        const options: ApexOptions = {
            chart: {
                height: 300,
                type: 'scatter' as const,
                toolbar: {
                    show: false
                },
                animations: {
                    enabled: true,
                    speed: 800
                },
                zoom: {
                    enabled: false,
                }
            },            
            colors: series.map(s => s.color),
            dataLabels: {
                enabled: false
            },
            fill: {
                opacity: 0.7
            },
            legend: {
                show: false
            },
            grid: {
                borderColor: '#f1f5f9',
                strokeDashArray: 3
            },
            markers: {
                size: 6,
                strokeColors: '#ffffff',
                strokeWidth: 1,
                hover: {
                    size: 8
                }
            },
            xaxis: {
                type: 'datetime',
                title: {
                    text: 'Timeline',
                    style: {
                        fontSize: '12px',
                        color: '#6b7280'
                    }
                },
                labels: {
                    show: false // Hide date labels
                },
                axisBorder: {
                    color: '#f1f5f9'
                },
                axisTicks: {
                    color: '#f1f5f9'
                }
            },
            yaxis: {
                title: {
                    text: 'Rating',
                    style: {
                        fontSize: '12px',
                        color: '#6b7280'
                    }
                },
                min: 1,
                max: 5,
                labels: {
                    formatter: function(value: number) {
                        return `${Math.round(value)}★`;
                    },
                    style: {
                        fontSize: '12px',
                        colors: '#6b7280'
                    }
                }
            }
        };

        return { series, options };
    }, [data]);

    if (!data || data.length === 0) {
        return (
            <div style={{ 
                padding: '2rem', 
                textAlign: 'center', 
                color: '#6b7280',
                fontSize: '0.875rem'
            }}>
                <EmptyMessageComponent
                    title={'No sentiment data available.'}
                    description={'There are no reviews with sentiment analysis in the selected range.'} />
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '300px' }}>
            <ReactApexChart 
                options={processedData.options}
                series={processedData.series}
                type="scatter"
                height={300}
            />
        </div>
    );
};

export default ScatterPlotSentiment;